import React from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";

ChartJS.register(...registerables);

interface ChartComponentProps {
  chartData: any; // incoming chart payload from backend
  title?: string;
  height?: number | string;
  editMode?: boolean;
  editData?: any;
  handleInputChange?: (
    id: string,
    field: string,
    value: any,
    componentType?: string
  ) => void;
  // Add save/edit control props
  toggleEditMode?: (id: string) => void;
  saveChanges?: (id: string) => void;
  cancelEdit?: (id: string) => void;
  saving?: Record<string, boolean>;
  item?: any; // Add item prop to get the ID
}

type NormalizedChartData = {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
  }>;
};

/**
 * Normalizes a variety of backend chart payloads into react-chartjs-2 data shape.
 * Supports:
 *  - { labels: [], data: [] }
 *  - { labels: [], datasets: [{ label, data, backgroundColor }] }
 *  - { series: { name: value, ... } } (object of key->number)
 *  - Custom colors via chartData.colors or chartData.chart.colors:
 *    - Array format: ["#FF6384", "#36A2EB", "#FFCE56"]
 *    - Object format: {P100: "#17191A", P75_S25: "#313B48", P50_S50: "#4A5C75", ...}
 *    (supports hex, rgb, rgba formats - automatically converts for background/border)
 */
function normalizeChartPayload(payload: any): NormalizedChartData {
  if (!payload) return { labels: [], datasets: [] };

  // Debug hint to make it easier to see incoming payload shapes in console
  // (remove or guard behind env var in production if noisy)
  // console.debug('normalizeChartPayload incoming:', payload);

  // If payload already contains datasets
  if (Array.isArray(payload.datasets) && payload.labels) {
    return { labels: payload.labels, datasets: payload.datasets };
  }

  // If payload has labels and a single data array
  if (Array.isArray(payload.labels) && Array.isArray(payload.data)) {
    return {
      labels: payload.labels,
      datasets: [
        {
          label: payload.title || "Dataset",
          data: payload.data,
          backgroundColor: payload.backgroundColor || "rgba(75, 192, 192, 0.2)",
          borderColor: payload.borderColor || "rgba(75, 192, 192, 1)",
        },
      ],
    };
  }

  // Common alternative key names for numeric arrays (no labels)
  const possibleDataKeys = [
    "data",
    "values",
    "counts",
    "series",
    "dataPoints",
    "y",
    "vals",
  ];
  for (const key of possibleDataKeys) {
    if (
      Array.isArray(payload[key]) &&
      payload[key].every((v: any) => typeof v === "number" || !isNaN(Number(v)))
    ) {
      const dataArr = payload[key].map((v: any) => Number(v));
      const labels =
        Array.isArray(payload.labels) &&
        payload.labels.length === dataArr.length
          ? payload.labels
          : dataArr.map((_value: number, i: number) => `Item ${i + 1}`);
      return {
        labels,
        datasets: [
          {
            label: payload.title || key || "Dataset",
            data: dataArr,
            backgroundColor:
              payload.backgroundColor || "rgba(75, 192, 192, 0.2)",
            borderColor: payload.borderColor || "rgba(75, 192, 192, 1)",
          },
        ],
      };
    }
  }

  // If payload is an object of key=>value (series-like)
  if (
    payload.series &&
    typeof payload.series === "object" &&
    !Array.isArray(payload.series)
  ) {
    const labels = Object.keys(payload.series);
    const data = labels.map((k) => Number(payload.series[k]) || 0);
    return {
      labels,
      datasets: [
        {
          label: payload.title || "Series",
          data,
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderColor: "rgba(75, 192, 192, 1)",
        },
      ],
    };
  }

  // If payload has chart object with labels/datasets
  if (
    payload.chart &&
    (payload.chart.labels || payload.chart.datasets || payload.chart.data)
  ) {
    return normalizeChartPayload(payload.chart);
  }

  // Handle chart objects created as series1/series2/... each containing key and value1..valueN
  if (payload.chart && typeof payload.chart === "object") {
    const chartObj = payload.chart;
    const seriesKeys = Object.keys(chartObj).filter(
      (k) => /^series/i.test(k) && typeof chartObj[k] === "object"
    );
    if (seriesKeys.length > 0) {
      const labels: string[] = [];
      const data: number[] = [];
      for (const sKey of seriesKeys) {
        const sObj = chartObj[sKey];
        const seriesLabel = sObj && sObj.key ? String(sObj.key) : sKey;
        labels.push(seriesLabel);

        const values = Object.keys(sObj || {})
          .filter((k) => /^value\d+/i.test(k))
          .map((k) => (sObj as any)[k])
          .filter((v) => v !== null && v !== undefined && v !== "");
        data.push(values.length);
      }
      return {
        labels,
        datasets: [
          {
            label: chartObj.title || payload.title || "Series Count",
            data,
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            borderColor: "rgba(75, 192, 192, 1)",
          },
        ],
      };
    }
  }

  // If payload is a simple map of string->number
  if (typeof payload === "object" && !Array.isArray(payload)) {
    const possibleLabels: string[] = [];
    const possibleData: number[] = [];
    for (const key of Object.keys(payload)) {
      const val = payload[key];
      if (typeof val === "number" || (!isNaN(Number(val)) && val !== null)) {
        possibleLabels.push(key);
        possibleData.push(Number(val));
      }
    }
    if (possibleLabels.length) {
      return {
        labels: possibleLabels,
        datasets: [
          {
            label: payload.title || "Data",
            data: possibleData,
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            borderColor: "rgba(75, 192, 192, 1)",
          },
        ],
      };
    }
  }

  // No plottable data found

  // Fallback empty shape
  return { labels: [], datasets: [] };
}

const ChartComponent: React.FC<ChartComponentProps> = ({
  chartData,
  title,
  height = 300,
  editMode = false,
  editData = {},
  handleInputChange,
  toggleEditMode,
  saveChanges,
  cancelEdit,
  saving,
  item,
}) => {
  // --- Editable grid state (like Graphs) ---
  const chartObj = chartData && chartData.chart ? chartData.chart : {};
  const [headers, setHeaders] = React.useState<string[]>([""]);
  const [rows, setRows] = React.useState<
    Array<{ label: string; services: string; series3: string }>
  >([{ label: "", services: "", series3: "" }]);
  const [series, setSeries] = React.useState<number>(1);
  const tableRef = React.useRef<HTMLDivElement | null>(null);
  const [isInitialDataLoad, setIsInitialDataLoad] = React.useState(true);
  const [showRemoveSeriesConfirm, setShowRemoveSeriesConfirm] =
    React.useState(false);
  const [showRemoveDataConfirm, setShowRemoveDataConfirm] =
    React.useState(false);
  const [chartTitle, setChartTitle] = React.useState(title || "");
  const [xAxisTitle, setXAxisTitle] = React.useState("Categories");
  const [yAxisTitle, setYAxisTitle] = React.useState("Values");
  const [chartType, setChartType] = React.useState<"bar" | "line" | "pie">(
    "bar"
  );

  // Initialize from chartData when component mounts
  React.useEffect(() => {
    // Always use chartData for initial loading
    const sourceData = chartData;
    const sourceChartObj =
      sourceData && sourceData.chart ? sourceData.chart : {};

    // Extract headers from chartObj (series1, series2, ...)
    const sKeys = Object.keys(sourceChartObj).filter((k) => /^series/i.test(k));
    const hdrs = sKeys.map((sk) => sourceChartObj[sk]?.key ?? "");

    // Determine series count based on actual data, minimum 1
    let actualSeriesCount = Math.max(sKeys.length, 1);

    // Pad headers array to match series count
    const paddedHeaders = [...hdrs];
    while (paddedHeaders.length < actualSeriesCount) {
      paddedHeaders.push("");
    }

    setHeaders(paddedHeaders);
    setSeries(actualSeriesCount);

    // Build rows from valueN fields
    let maxRows = 0;
    const allSeriesVals: string[][] = [];

    for (let seriesIndex = 0; seriesIndex < actualSeriesCount; seriesIndex++) {
      const sKey = `series${seriesIndex + 1}`;
      const s = sourceChartObj[sKey] || {};
      const vals = Object.keys(s)
        .filter((k) => /^value\d+/i.test(k))
        .sort(
          (a, b) =>
            (Number(a.replace(/\D/g, "")) || 0) -
            (Number(b.replace(/\D/g, "")) || 0)
        )
        .map((k) => s[k] ?? "");
      maxRows = Math.max(maxRows, vals.length);
      allSeriesVals.push(vals);
    }

    // Build row objects - only include rows with actual data
    const matrix: Array<{ label: string; services: string; series3: string }> =
      [];
    for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
      const rowData = {
        label: allSeriesVals[0]?.[rowIndex] ?? "",
        services: allSeriesVals[1]?.[rowIndex] ?? "",
        series3: allSeriesVals[2]?.[rowIndex] ?? "",
      };

      // Only add rows that have some data (at least a label or some values)
      if (
        rowData.label.trim() !== "" ||
        rowData.services.trim() !== "" ||
        rowData.series3.trim() !== ""
      ) {
        matrix.push(rowData);
      }
    }

    // Ensure at least one row for editing if no data exists
    if (matrix.length === 0) {
      matrix.push({ label: "", services: "", series3: "" });
    }

    console.log("ChartComponent: Initializing with data", {
      sourceData,
      sourceChartObj,
      editMode,
      editData,
      extractedHeaders: hdrs,
      maxRows,
      allSeriesVals,
      finalMatrix: matrix,
    });

    setRows(matrix);
    setIsInitialDataLoad(true);

    // Initialize chart title and axis labels from data
    if (sourceData?.title) {
      setChartTitle(sourceData.title);
    }

    // Initialize chart type from data
    if (sourceData?.chartType) {
      const type = sourceData.chartType.toLowerCase();
      if (type === "bar" || type === "line" || type === "pie") {
        setChartType(type as "bar" | "line" | "pie");
      }
    }

    // Use series keys as axis titles
    // series1.key = X-axis title (categories)
    // series2.key = Y-axis title (values)
    if (hdrs[0] && hdrs[0].trim() !== "") {
      setXAxisTitle(hdrs[0]);
    }
    if (hdrs[1] && hdrs[1].trim() !== "") {
      setYAxisTitle(hdrs[1]);
    }

    // Fallback to explicit axis titles if provided
    if (sourceData?.xAxisTitle) {
      setXAxisTitle(sourceData.xAxisTitle);
    }
    if (sourceData?.yAxisTitle) {
      setYAxisTitle(sourceData.yAxisTitle);
    }
  }, [chartData]); // Only run when chartData changes initially

  // Separate effect to initialize from editData when entering edit mode
  React.useEffect(() => {
    // Only initialize from editData when first entering edit mode
    if (editMode && editData?.chart && editData.chart.chart) {
      const sourceChartObj = editData.chart.chart;
      console.log("ChartComponent: Initializing from editData", {
        sourceChartObj,
      });

      // Extract headers from editData
      const sKeys = Object.keys(sourceChartObj).filter((k) =>
        /^series/i.test(k)
      );
      const hdrs = sKeys.map((sk) => sourceChartObj[sk]?.key ?? "");

      if (hdrs.length > 0 && hdrs.some((h) => h !== "")) {
        // Only update if we have meaningful data
        const actualSeriesCount = Math.max(sKeys.length, 1);
        const paddedHeaders = [...hdrs];
        while (paddedHeaders.length < actualSeriesCount) {
          paddedHeaders.push("");
        }
        setHeaders(paddedHeaders);
        setSeries(actualSeriesCount);

        // Build rows from editData
        let maxRows = 0;
        const allSeriesVals: string[][] = [];

        for (
          let seriesIndex = 0;
          seriesIndex < actualSeriesCount;
          seriesIndex++
        ) {
          const sKey = `series${seriesIndex + 1}`;
          const s = sourceChartObj[sKey] || {};
          const vals = Object.keys(s)
            .filter((k) => /^value\d+/i.test(k))
            .sort(
              (a, b) =>
                (Number(a.replace(/\D/g, "")) || 0) -
                (Number(b.replace(/\D/g, "")) || 0)
            )
            .map((k) => s[k] ?? "");
          maxRows = Math.max(maxRows, vals.length);
          allSeriesVals.push(vals);
        }

        if (maxRows > 0) {
          const matrix: Array<{
            label: string;
            services: string;
            series3: string;
          }> = [];
          for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
            const rowData = {
              label: allSeriesVals[0]?.[rowIndex] ?? "",
              services: allSeriesVals[1]?.[rowIndex] ?? "",
              series3: allSeriesVals[2]?.[rowIndex] ?? "",
            };

            // Only add rows that have some data
            if (
              rowData.label.trim() !== "" ||
              rowData.services.trim() !== "" ||
              rowData.series3.trim() !== ""
            ) {
              matrix.push(rowData);
            }
          }

          // Ensure at least one row for editing
          if (matrix.length === 0) {
            matrix.push({ label: "", services: "", series3: "" });
          }

          setRows(matrix);
        }

        // Also update axis titles from series keys in editData
        if (hdrs[0] && hdrs[0].trim() !== "") {
          setXAxisTitle(hdrs[0]);
        }
        if (hdrs[1] && hdrs[1].trim() !== "") {
          setYAxisTitle(hdrs[1]);
        }
      }
    }
  }, [editMode]); // Only run when editMode changes

  React.useEffect(() => {
    if (tableRef.current && isInitialDataLoad) {
      requestAnimationFrame(() => {
  tableRef.current?.scrollTo({ top: 0, behavior: "auto" });
      });
    }
  }, [rows.length, isInitialDataLoad]);

  // Handlers
  const handleHeaderChange = (index: number, value: string) => {
    if (value.length > 25) return;
    console.log("ChartComponent: Header change", {
      index,
      value,
      currentHeaders: headers,
    });
    setHeaders((prev) => {
      const newHeaders = prev.map((h, i) => (i === index ? value : h));
      console.log("ChartComponent: Setting new headers", newHeaders);

      // Automatically update axis titles based on series keys
      // series1.key (index 0) = X-axis title
      // series2.key (index 1) = Y-axis title
      if (index === 0) {
        setXAxisTitle(value || "Categories");
      } else if (index === 1) {
        setYAxisTitle(value || "Values");
      }

      return newHeaders;
    });
    if (handleInputChange) {
      const sKey = `series${index + 1}`;
      console.log("ChartComponent: Calling handleInputChange for header", {
        field: `chart.chart.${sKey}.key`,
        value,
      });
      handleInputChange(
        item?._id || "",
        `chart.chart.${sKey}.key`,
        value,
        "Chart"
      );

      // Also save the axis titles
      if (index === 0) {
        handleInputChange(
          item?._id || "",
          "chart.xAxisTitle",
          value || "Categories",
          "Chart"
        );
      } else if (index === 1) {
        handleInputChange(
          item?._id || "",
          "chart.yAxisTitle",
          value || "Values",
          "Chart"
        );
      }
    }
  };

  const handleRowInput = (rowIndex: number, column: string, value: string) => {
    if (value.length > 25) return;

    // Allow text in label column, only numbers in data columns
    if (column !== "label" && value !== "" && !/^[\d.]*$/.test(value)) return;

    console.log("ChartComponent: Row input change", {
      rowIndex,
      column,
      value,
      currentRows: rows,
    });
    setRows((prev) => {
      const newRows = prev.map((row, idx) =>
        idx === rowIndex ? { ...row, [column]: value } : row
      );
      console.log("ChartComponent: Setting new rows", newRows);
      return newRows;
    });

    if (handleInputChange) {
      let sKey = column;
      if (column === "label") sKey = "series1";
      else if (column === "services") sKey = "series2";
      else if (column === "series3") sKey = "series3";
      console.log("ChartComponent: Calling handleInputChange for row", {
        field: `chart.chart.${sKey}.value${rowIndex + 1}`,
        value,
      });
      handleInputChange(
        item?._id || "",
        `chart.chart.${sKey}.value${rowIndex + 1}`,
        value,
        "Chart"
      );
    }
  };

  const addRow = () => {
    if (rows.length < 10) {
      setIsInitialDataLoad(false);
      setRows((prev) => [...prev, { label: "", services: "", series3: "" }]);
      // notify parent
      if (handleInputChange) {
        if (series >= 1)
          handleInputChange(
            item?._id || "",
            `chart.chart.series1.value${rows.length + 1}`,
            "",
            "Chart"
          );
        if (series >= 2)
          handleInputChange(
            item?._id || "",
            `chart.chart.series2.value${rows.length + 1}`,
            "",
            "Chart"
          );
        if (series >= 3)
          handleInputChange(
            item?._id || "",
            `chart.chart.series3.value${rows.length + 1}`,
            "",
            "Chart"
          );
      }
    }
  };

  const removeLastRow = () => {
    if (rows.length > 1) {
      setRows((prev) => prev.slice(0, -1));
      // Notify parent to clear last value
      if (handleInputChange) {
        const lastRowIndex = rows.length;
        if (series >= 1)
          handleInputChange(
            item?._id || "",
            `chart.chart.series1.value${lastRowIndex}`,
            "",
            "Chart"
          );
        if (series >= 2)
          handleInputChange(
            item?._id || "",
            `chart.chart.series2.value${lastRowIndex}`,
            "",
            "Chart"
          );
        if (series >= 3)
          handleInputChange(
            item?._id || "",
            `chart.chart.series3.value${lastRowIndex}`,
            "",
            "Chart"
          );
      }
    }
    setShowRemoveDataConfirm(false);
  };

  const addSeries = () => {
    const maxSeries = 3;
    if (series < maxSeries) {
      const newSeriesCount = series + 1;
      setSeries(newSeriesCount);
      setHeaders((prev) => [...prev, ""]);

      // Update existing rows to include the new series field - force new array to trigger re-render
      setRows((prev) =>
        prev.map((row) => ({
          label: row.label,
          services: row.services || "", // Always ensure services field exists
          series3: row.series3 || "", // Always ensure series3 field exists
        }))
      );

      // Notify parent to add new series structure
      if (handleInputChange) {
        const newSeriesKey = `series${newSeriesCount}`;
        handleInputChange(
          item?._id || "",
          `chart.chart.${newSeriesKey}.key`,
          "",
          "Chart"
        );
        // Add empty values for existing rows
        for (let i = 0; i < rows.length; i++) {
          handleInputChange(
            item?._id || "",
            `chart.chart.${newSeriesKey}.value${i + 1}`,
            "",
            "Chart"
          );
        }
      }
    }
  };

  const removeSeries = () => {
    if (series > 2) {
      const newSeriesCount = series - 1;
      setSeries(newSeriesCount);
      setHeaders((prev) => prev.slice(0, -1));

      // Remove series3 data from rows if going from 3 to 2 series
      if (newSeriesCount === 2) {
        setRows((prev) =>
          prev.map((row) => ({
            label: row.label,
            services: row.services,
            series3: "", // Clear series3 data
          }))
        );
      }

      // Notify parent to remove series structure
      if (handleInputChange) {
        const removedSeriesKey = `series${series}`;
        handleInputChange(
          item?._id || "",
          `chart.chart.${removedSeriesKey}.key`,
          "",
          "Chart"
        );
        // Clear values for the removed series
        for (let i = 0; i < rows.length; i++) {
          handleInputChange(
            item?._id || "",
            `chart.chart.${removedSeriesKey}.value${i + 1}`,
            "",
            "Chart"
          );
        }
      }
    }
    setShowRemoveSeriesConfirm(false);
  };

  // Create chart data from current state (works for both edit and view modes)
  const createLiveChartData = () => {
    const datasets = [];

    // Define sourceChartObj first
    const sourceChartObj = editMode ? (editData?.chart?.chart || chartData?.chart || {}) : (chartData?.chart || {});

    // Default colors - using the exact color scheme you specified
    const defaultColors = [
      "#17191A", // P100
      "#313B48", // P75_S25
      "#4A5C75", // P50_S50
      "#647EA3", // P25_S75
      "#7D9FD0", // S100
      "#E8E8E8", // F_P100
      "#E4E8EC", // F_P75_S25
      "#FFFFFF", // F_P50_S50
      "#FFFFFF", // F_P25_S75
      "#FFFFFF", // F_S100
      "#FFFFFF", // SCL
      "#000000"  // SCD
    ];

    // Check if colors are provided in the chart data (use editData colors in edit mode)
    const customColorsData = sourceChartObj.colors || (editMode ? editData?.chart?.colors : null) || chartData?.colors;

    let colors = defaultColors;

    // Handle colors as object (e.g., {P100: "#17191A", P75_S25: "#313B48", ...})
    if (
      customColorsData &&
      typeof customColorsData === "object" &&
      !Array.isArray(customColorsData)
    ) {
      // Extract colors from object - prioritize primary colors for chart series
      const colorKeys = Object.keys(customColorsData);
      const extractedColors = [];

      // Priority order for selecting colors from the object - use more variety for pie charts
      const priorityKeys = ["P100", "S100", "F_P100", "P75_S25", "P50_S50", "P25_S75", "F_P75_S25", "F_P50_S50", "F_P25_S75", "F_S100", "SCL", "SCD"];

      // First, try to get colors from priority keys
      for (const key of priorityKeys) {
        if (customColorsData[key]) {
          extractedColors.push(customColorsData[key]);
        }
      }

      // If we don't have enough colors, add any remaining colors from the object
      for (const key of colorKeys) {
        if (!priorityKeys.includes(key) && extractedColors.length < 6) {
          extractedColors.push(customColorsData[key]);
        }
      }

      if (extractedColors.length > 0) {
        colors = extractedColors;
        console.log(
          "ChartComponent: Using custom colors from object:",
          extractedColors
        );
      } else {
        console.log(
          "ChartComponent: No valid colors found in object, using defaults"
        );
      }
    }
    // Handle colors as array (existing functionality)
    else if (customColorsData && Array.isArray(customColorsData)) {
      colors = customColorsData;
      console.log(
        "ChartComponent: Using custom colors from array:",
        customColorsData
      );
    } else {
      console.log("ChartComponent: Using default colors");
    }

    // Helper function to ensure color has proper alpha for background
    const getBackgroundColor = (color: string, index: number) => {
      if (!color) return defaultColors[index] || "rgba(75, 192, 192, 0.6)";

      // If color already has alpha (rgba), use as is
      if (color.includes("rgba")) return color;

      // If color is rgb, convert to rgba with 0.6 alpha
      if (color.includes("rgb(")) {
        return color.replace("rgb(", "rgba(").replace(")", ", 0.6)");
      }

      // If color is hex, convert to rgba
      if (color.startsWith("#")) {
        const hex = color.replace("#", "");
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return `rgba(${r}, ${g}, ${b}, 0.6)`;
      }

      // Fallback to default
      return defaultColors[index] || "rgba(75, 192, 192, 0.6)";
    };

    // Helper function to get border color (full opacity)
    const getBorderColor = (color: string, index: number) => {
      if (!color)
        return (
          defaultColors[index]?.replace("0.6", "1") || "rgba(75, 192, 192, 1)"
        );

      // If color already has alpha, set to 1
      if (color.includes("rgba")) {
        return color.replace(/,\s*[\d.]+\)$/, ", 1)");
      }

      // If color is rgb, use as is (already full opacity)
      if (color.includes("rgb(")) return color;

      // If color is hex, convert to rgb
      if (color.startsWith("#")) {
        const hex = color.replace("#", "");
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return `rgb(${r}, ${g}, ${b})`;
      }

      // Fallback to default
      return (
        defaultColors[index]?.replace("0.6", "1") || "rgba(75, 192, 192, 1)"
      );
    };

    // Get data source based on mode
    let dataHeaders = headers;
    let dataRows = rows;
    let dataSeries = series;

    if (!editMode) {
      // When not in edit mode, extract data from chartData
      const sKeys = Object.keys(sourceChartObj).filter((k) =>
        /^series/i.test(k)
      );
      dataHeaders = sKeys.map((sk) => sourceChartObj[sk]?.key ?? "");
      dataSeries = Math.max(sKeys.length, 1);

      // Build rows from chartData
      let maxRows = 0;
      const allSeriesVals: string[][] = [];

      for (let seriesIndex = 0; seriesIndex < dataSeries; seriesIndex++) {
        const sKey = `series${seriesIndex + 1}`;
        const s = sourceChartObj[sKey] || {};
        const vals = Object.keys(s)
          .filter((k) => /^value\d+/i.test(k))
          .sort(
            (a, b) =>
              (Number(a.replace(/\D/g, "")) || 0) -
              (Number(b.replace(/\D/g, "")) || 0)
          )
          .map((k) => s[k] ?? "");
        maxRows = Math.max(maxRows, vals.length);
        allSeriesVals.push(vals);
      }

      dataRows = [];
      for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
        dataRows.push({
          label: allSeriesVals[0]?.[rowIndex] ?? "",
          services: allSeriesVals[1]?.[rowIndex] ?? "",
          series3: allSeriesVals[2]?.[rowIndex] ?? "",
        });
      }
    }

    // Filter out rows that don't have labels or have empty labels
    const validRows = dataRows.filter(
      (row) => row.label && row.label.trim() !== ""
    );

    // Create labels from valid rows only
    const labels = validRows.map((row) => row.label);

    // Skip first series (index 0) as it contains labels, start from series 1 (data columns)
    for (let seriesIndex = 1; seriesIndex < dataSeries; seriesIndex++) {
      const seriesLabel = dataHeaders[seriesIndex];

      // Only process series that have non-empty labels
      if (!seriesLabel || seriesLabel.trim() === "") continue;

      const data = [];

      // Get numeric data from valid rows for this series
      for (let rowIndex = 0; rowIndex < validRows.length; rowIndex++) {
        let value = 0;
        if (seriesIndex === 1)
          value = parseFloat(validRows[rowIndex].services) || 0;
        else if (seriesIndex === 2)
          value = parseFloat(validRows[rowIndex].series3) || 0;
        data.push(value);
      }

      // Only add dataset if it has some non-zero data or meaningful label
      if (data.some((d) => d > 0) || seriesLabel.trim() !== "") {
        // For pie charts, use different colors for each slice
        const backgroundColors = [];
        const borderColors = [];
        
        if (chartType === "pie") {
          // Use different colors for each data point (slice)
          for (let i = 0; i < data.length; i++) {
            backgroundColors.push(getBackgroundColor(colors[i % colors.length], i));
            borderColors.push(getBorderColor(colors[i % colors.length], i));
          }
        } else {
          // For bar/line charts, use single color per dataset
          backgroundColors.push(getBackgroundColor(colors[seriesIndex - 1], seriesIndex - 1));
          borderColors.push(getBorderColor(colors[seriesIndex - 1], seriesIndex - 1));
        }
        
        datasets.push({
          label: seriesLabel,
          data: data,
          backgroundColor: chartType === "pie" ? backgroundColors : backgroundColors[0],
          borderColor: chartType === "pie" ? borderColors : borderColors[0],
          borderWidth: 1,
        });
      }
    }

    return { labels, datasets };
  };

  // Recalculate chart data whenever headers, rows, or series change
  const normalizedForRender = React.useMemo(() => {
    const chartData = createLiveChartData();
    console.log("ChartComponent: Chart data for rendering", chartData);
    return chartData;
  }, [headers, rows, series, editMode, chartData, chartType]);

  // console.log('ChartComponent: Final chart data', normalizedForRender);
  // console.log('ChartComponent: Current state', {
  //   editMode,
  //   headers,
  //   rows,
  //   series,
  //   editData,
  //   itemId: item?._id,
  //   hasSaveChanges: !!saveChanges,
  //   hasToggleEditMode: !!toggleEditMode,
  //   hasCancelEdit: !!cancelEdit
  // });

  const options = React.useMemo(() => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: false, // Disable Chart.js title since we're using HTML title
        },
        legend: {
          display: true,
          position: "top" as const,
        },
      },
    };

    // Pie charts don't have axes
    if (chartType === "pie") {
      return baseOptions;
    }

    // Bar and Line charts have axes
    return {
      ...baseOptions,
      scales: {
        x: {
          title: {
            display: !!xAxisTitle,
            text: xAxisTitle || "Categories",
            font: {
              size: 14,
              weight: "bold" as const,
            },
          },
          ticks: {
            maxRotation: 45,
            minRotation: 0,
          },
        },
        y: {
          title: {
            display: !!yAxisTitle,
            text: yAxisTitle || "Values",
            font: {
              size: 14,
              weight: "bold" as const,
            },
          },
          beginAtZero: true,
          ticks: {
            callback: function (value: any) {
              // Format large numbers with K, M suffixes
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + "M";
              } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + "K";
              }
              return value;
            },
          },
        },
      },
    };
  }, [chartTitle, xAxisTitle, yAxisTitle, chartType]);

  return (
    <div
      className="chart-component"
      style={{
        width: "100%",
        minHeight: typeof height === "number" ? `${height}px` : height,
      }}
    >
      {/* Title matching other components */}
      {chartTitle && (
        <h4 className="text-lg font-bold mb-2">
          {chartTitle}
        </h4>
      )}
      
      <div
        style={{ height: typeof height === "number" ? `${height}px` : height }}
      >
        {Array.isArray(normalizedForRender.datasets) &&
        normalizedForRender.datasets.length > 0 ? (
          <>
            {chartType === "bar" && (
              <Bar data={normalizedForRender} options={options as any} />
            )}
            {chartType === "line" && (
              <Line data={normalizedForRender} options={options as any} />
            )}
            {chartType === "pie" && (
              <Pie data={normalizedForRender} options={options as any} />
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded">
            No chart data available
          </div>
        )}
      </div>

      {/* Editable UI - table-like editor inspired by your reference */}
      {editMode && (
        <div className="mt-4 w-full bg-white p-3 border rounded">
          <h5 className="font-semibold mb-4">Edit Chart</h5>

          {/* Chart Title and Axis Labels */}
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <h6 className="font-medium mb-2">Chart Configuration</h6>

            {/* Chart Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chart Type
              </label>
              <div className="flex gap-2">
                {(["bar", "line", "pie"] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      setChartType(type);
                      if (handleInputChange) {
                        handleInputChange(
                          item?._id || "",
                          "chart.chartType",
                          type,
                          "Chart"
                        );
                      }
                    }}
                    className={`px-4 py-2 rounded-md border text-sm font-medium capitalize transition-colors ${
                      chartType === type
                        ? "bg-blue-500 text-white border-blue-500"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {chartType !== "pie" && (
              <p className="text-sm text-gray-600 mb-3">
                Axis labels are automatically synced with Series 1 (X-axis) and
                Series 2 (Y-axis) names below.
              </p>
            )}
            <div
              className={`grid gap-3 ${
                chartType === "pie"
                  ? "grid-cols-1"
                  : "grid-cols-1 md:grid-cols-3"
              }`}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chart Title
                </label>
                <input
                  type="text"
                  value={chartTitle}
                  placeholder="Enter chart title"
                  onChange={(e) => {
                    setChartTitle(e.target.value);
                    if (handleInputChange) {
                      handleInputChange(
                        item?._id || "",
                        "chart.title",
                        e.target.value,
                        "Chart"
                      );
                    }
                  }}
                  className="w-full rounded px-3 py-2 border border-gray-300 focus:border-blue-500 focus:outline-none"
                />
              </div>
              {chartType !== "pie" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      X-Axis Label
                    </label>
                    <input
                      type="text"
                      value={xAxisTitle}
                      placeholder="X-axis label (synced with Series 1)"
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setXAxisTitle(newValue);
                        // Also update the first series key (series1) to keep them in sync
                        setHeaders((prev) => {
                          const newHeaders = [...prev];
                          newHeaders[0] = newValue;
                          return newHeaders;
                        });
                        if (handleInputChange) {
                          handleInputChange(
                            item?._id || "",
                            "chart.xAxisTitle",
                            newValue,
                            "Chart"
                          );
                          handleInputChange(
                            item?._id || "",
                            "chart.chart.series1.key",
                            newValue,
                            "Chart"
                          );
                        }
                      }}
                      className="w-full rounded px-3 py-2 border border-gray-300 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Y-Axis Label
                    </label>
                    <input
                      type="text"
                      value={yAxisTitle}
                      placeholder="Y-axis label (synced with Series 2)"
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setYAxisTitle(newValue);
                        // Also update the second series key (series2) to keep them in sync
                        setHeaders((prev) => {
                          const newHeaders = [...prev];
                          newHeaders[1] = newValue;
                          return newHeaders;
                        });
                        if (handleInputChange) {
                          handleInputChange(
                            item?._id || "",
                            "chart.yAxisTitle",
                            newValue,
                            "Chart"
                          );
                          handleInputChange(
                            item?._id || "",
                            "chart.chart.series2.key",
                            newValue,
                            "Chart"
                          );
                        }
                      }}
                      className="w-full rounded px-3 py-2 border border-gray-300 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <h6 className="font-medium mb-2">Chart Data</h6>
          <div
            ref={tableRef}
            className="overflow-x-auto overflow-y-auto max-h-80"
          >
            <table className="table-auto w-full border-collapse">
              <thead>
                <tr className="bg-[#F5F7FA]">
                  {Array.from({ length: series }).map((_, index) => (
                    <th key={index} className="px-2 py-2 text-left">
                      <input
                        type="text"
                        value={headers[index] ?? ""}
                        placeholder={`Enter Series ${index + 1}`}
                        onChange={(e) =>
                          handleHeaderChange(index, e.target.value)
                        }
                        className="w-full min-w-[100px] rounded px-2 py-1 outline-none bg-white border border-gray-300 focus:border-blue-500"
                        maxLength={25}
                        disabled={!editMode}
                      />
                    </th>
                  ))}
                  <th className="py-2 text-right">
                    <div className="flex gap-2 justify-end">
                      {series < 3 && (
                        <button
                          onClick={addSeries}
                          className="flex items-center px-2 sm:px-3 py-2 bg-[#F5F7FA] rounded text-sm"
                        >
                          <span className="mr-1 sm:mr-2 text-lg">+</span>
                          <span className="hidden sm:inline">
                            Add New Series
                          </span>
                          <span className="sm:hidden">Add</span>
                        </button>
                      )}
                      {series >= 3 && (
                        <button
                          onClick={() => setShowRemoveSeriesConfirm(true)}
                          className="flex items-center px-2 sm:px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 text-sm"
                        >
                          <span className="mr-1 sm:mr-2 text-lg">−</span>
                          <span className="hidden sm:inline">
                            Remove Series
                          </span>
                          <span className="sm:hidden">Remove</span>
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={`row-${rowIndex}-${series}`}>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={row.label}
                        placeholder={`Enter Label ${rowIndex + 1}`}
                        onChange={(e) =>
                          handleRowInput(rowIndex, "label", e.target.value)
                        }
                        className="w-full min-w-[100px] rounded px-2 py-1 outline-none bg-white border border-gray-300 focus:border-blue-500"
                        maxLength={25}
                        disabled={!editMode}
                      />
                    </td>
                    {series >= 2 && (
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={row.services}
                          placeholder={`Enter Value ${rowIndex + 1}`}
                          onChange={(e) =>
                            handleRowInput(rowIndex, "services", e.target.value)
                          }
                          className="w-full min-w-[100px] rounded px-2 py-1 outline-none bg-white border border-gray-300 focus:border-blue-500"
                          maxLength={25}
                          disabled={!editMode}
                        />
                      </td>
                    )}
                    {series >= 3 && (
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={row.series3}
                          placeholder={`Enter Value ${rowIndex + 1}`}
                          onChange={(e) =>
                            handleRowInput(rowIndex, "series3", e.target.value)
                          }
                          className="w-full min-w-[100px] rounded px-2 py-1 outline-none bg-white border border-gray-300 focus:border-blue-500"
                          maxLength={25}
                          disabled={!editMode}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={addRow}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-[#E1E3E5] rounded text-sm"
              disabled={rows.length >= 10}
            >
              <span className="text-lg">+</span>
              <span className="hidden sm:inline">Add Data</span>
              <span className="sm:hidden">Add</span>
            </button>
            <button
              onClick={() => rows.length > 1 && setShowRemoveDataConfirm(true)}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded text-sm ${
                rows.length <= 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-white hover:bg-red-50"
              }`}
              disabled={rows.length <= 1}
            >
              <span className="text-lg">−</span>
              <span className="hidden sm:inline">Remove Data</span>
              <span className="sm:hidden">Remove</span>
            </button>
            <div className="ml-auto text-sm text-gray-500 self-center">
              {rows.length}/10 rows
            </div>
          </div>

          {/* Save/Cancel buttons */}
          {/* {item?._id && (saveChanges || cancelEdit) && (
            <div className="flex flex-row justify-center gap-2 mt-4 pt-3 border-t border-gray-200">
              {saveChanges && (
                <button 
                  onClick={() => {
                    console.log('ChartComponent: Save button clicked', { 
                      itemId: item._id, 
                      headers, 
                      rows, 
                      series,
                      editData,
                      chartData 
                    });
                    if (saveChanges) {
                      saveChanges(item._id);
                    } else {
                      console.error('ChartComponent: saveChanges function not provided');
                    }
                  }}
                  disabled={saving?.[item._id]}
                  className={`px-4 py-2 rounded text-white min-w-[100px] ${saving?.[item._id] ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {saving?.[item._id] ? 'Saving...' : 'Save'}
                </button>
              )}
              {cancelEdit && (
                <button 
                  onClick={() => cancelEdit(item._id)}
                  className="px-4 py-2 rounded text-white bg-gray-600 hover:bg-gray-700 min-w-[100px]"
                >
                  Cancel
                </button>
              )}
            </div>
          )} */}
        </div>
      )}

      {/* Confirmation Dialogs */}
      {showRemoveSeriesConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Remove Series</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove the last series? This will delete
              all data in that series.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRemoveSeriesConfirm(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={removeSeries}
                className="px-4 py-2 text-white bg-red-600 rounded hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {showRemoveDataConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Remove Data Row</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove the last data row?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRemoveDataConfirm(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={removeLastRow}
                className="px-4 py-2 text-white bg-red-600 rounded hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Log missing-data payloads in dev mode to help debugging without breaking JSX
if (process.env.NODE_ENV !== "production") {
  // Note: avoid logging in SSR or environments where window isn't defined
  try {
    // This module-level log will not have access to specific instances, so keep it lightweight.
  } catch (e) {
    // noop
  }
}

export default ChartComponent;
